import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import { useComposerDraftStore } from "../composerDraftStore";
import { useProjects } from "../state/entities";
import { usePrimaryEnvironmentId } from "../state/environments";
import { useActiveOrgId } from "../state/orgs";
import { requestThreadSpace } from "../state/spaces";
import { buildDraftThreadRouteParams } from "../threadRoutes";
import { useNewThreadHandler } from "./useHandleNewThread";

/**
 * New chat in a space: opens the organization's new-chat draft in Chat (the
 * one draft Home and Chat share) already filed in the space. The choice is
 * the composer strip's own (state/spaces requestThreadSpace), so the strip
 * shows the space and can still change it; the chat is filed once its thread
 * exists (usePendingThreadSpaces). Null until the organization's workspace
 * exists, as Chat itself waits for it.
 */
export function useStartSpaceChat(): ((spaceId: string) => Promise<void>) | null {
  const orgId = useActiveOrgId();
  const environmentId = usePrimaryEnvironmentId();
  const projects = useProjects();
  const handleNewThread = useNewThreadHandler();
  const navigate = useNavigate();
  const orgProject = useMemo(
    () => projects.find((project) => project.id === orgId && project.environmentId === environmentId) ?? null,
    [environmentId, orgId, projects],
  );
  const start = useCallback(
    async (spaceId: string) => {
      if (orgId === null || orgProject === null) return;
      const draftId = await handleNewThread(scopeProjectRef(orgProject.environmentId, orgProject.id), {
        navigate: false,
      });
      if (draftId === null) return;
      const threadId = useComposerDraftStore.getState().getDraftSession(draftId)?.threadId ?? null;
      if (threadId !== null) requestThreadSpace(threadId, orgId, spaceId);
      await navigate({ to: "/$org/draft/$draftId", params: buildDraftThreadRouteParams(draftId, orgProject.id) });
    },
    [handleNewThread, navigate, orgId, orgProject],
  );
  return orgProject === null ? null : start;
}
